import React from "react";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent } from "@shared/components/ui/card";
import { financial, formatInvoiceStatus } from "@shared/utils/helper";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const InvoiceStatusTable = ({ data, isLoading = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Handle loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-muted rounded"></div>
                  <div className="w-16 h-5 bg-muted rounded"></div>
                </div>
                <div className="w-8 h-8 bg-muted rounded"></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="w-12 h-3 bg-muted rounded mb-1"></div>
                  <div className="w-8 h-6 bg-muted rounded"></div>
                </div>
                <div className="text-right">
                  <div className="w-12 h-3 bg-muted rounded mb-1 ml-auto"></div>
                  <div className="w-16 h-6 bg-muted rounded ml-auto"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Handle empty data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card className="text-center py-8">
        <CardContent>
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">
            {t("invoices.noData") || "No Invoice Data"}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t("invoices.noDataDescription") ||
              "No invoices found for the selected period"}
          </p>
          <Button variant="outline" onClick={() => navigate("/invoices")}>
            <Eye className="h-4 w-4 mr-2" />
            {t("invoices.viewAll") || "View All Invoices"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const formattedData = formatInvoiceStatus(data);

  const statusIcons = {
    paid: <CheckCircle2 className="h-4 w-4 text-green-500" />,
    pending: <Clock className="h-4 w-4 text-yellow-500" />,
    overdue: <AlertCircle className="h-4 w-4 text-red-500" />,
    cancelled: <AlertCircle className="h-4 w-4 text-gray-500" />,
  };

  const statusVariants = {
    paid: "default",
    pending: "secondary",
    overdue: "destructive",
    cancelled: "outline",
  };

  const statusColors = {
    paid: "text-green-600 bg-green-50 dark:bg-green-950 border-green-200",
    pending:
      "text-yellow-600 bg-yellow-50 dark:bg-yellow-950 border-yellow-200",
    overdue: "text-red-600 bg-red-50 dark:bg-red-950 border-red-200",
    cancelled: "text-gray-600 bg-gray-50 dark:bg-gray-950 border-gray-200",
  };

  // Calculate totals and percentages
  const totalInvoices = formattedData.reduce(
    (acc, status) => acc + status.value,
    0
  );
  const totalAmount = formattedData.reduce(
    (acc, status) => acc + status.totalNet,
    0
  );

  // Chart view for all devices
  return (
    <div className="space-y-3">
      {/* Compact Invoice Status Chart */}
      <div className="space-y-2">
        {formattedData.map((status) => {
          const percentage =
            totalInvoices > 0
              ? ((status.value / totalInvoices) * 100).toFixed(1)
              : "0";
          const amountPercentage =
            totalAmount > 0
              ? ((status.totalNet / totalAmount) * 100).toFixed(1)
              : "0";

          return (
            <Card
              key={status.label}
              className={`transition-all hover:shadow-sm border-l-4 ${
                statusColors[status.label]
              }`}
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    {statusIcons[status.label]}
                    <Badge
                      variant={statusVariants[status.label]}
                      className="gap-1 text-xs"
                    >
                      {t(`status.${status.label}`)}
                    </Badge>
                    <span className="text-sm font-medium">{status.value}</span>
                    <span className="text-xs text-muted-foreground">
                      ({percentage}%)
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-sm font-semibold">
                        {financial(status.totalNet)}{" "}
                        {t("main.currency") || "MAD"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {amountPercentage}%
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        navigate(`/invoices?status=${status.label}`)
                      }
                      className="h-7 w-7 p-0 hover:bg-primary/10"
                    >
                      <Eye className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                {/* Compact Progress Bar */}
                <div className="mt-2">
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        status.label === "paid"
                          ? "bg-green-500"
                          : status.label === "pending"
                          ? "bg-yellow-500"
                          : status.label === "cancelled"
                          ? "bg-gray-500"
                          : "bg-red-500"
                      }`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Compact Summary */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-blue-200 dark:border-blue-800">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="font-medium text-blue-900 dark:text-blue-100 text-sm">
                {t("main.total") || "Total"}
              </span>
              <span className="text-sm font-bold text-blue-900 dark:text-blue-100">
                {totalInvoices}
              </span>
              <span className="text-xs text-blue-700 dark:text-blue-300">
                {t("invoices.invoices").toLowerCase()}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-bold text-blue-900 dark:text-blue-100">
                  {financial(totalAmount)} {t("main.currency") || "MAD"}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/invoices")}
                className="h-7 border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900 text-xs"
              >
                <Eye className="h-3 w-3 mr-1" />
                {t("common.viewAll") || "View All"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default React.memo(InvoiceStatusTable);
